import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Grid,
  FormControlLabel,
  Switch,
} from "@mui/material"
import { z } from "zod"
import { useAddModel } from "@/hooks/useAddModel"
import ClearIcon from "@mui/icons-material/Clear"
import { useEffect } from "react"
import MultiSelect from "@/components/Select/MultipleSelect"
import useTags from "@/hooks/useTags"
import { toast } from "sonner"

const TagSchema = z.object({
  id: z.number().positive(),
  name: z.string(),
})

const CategorySchema = z.object({
  name: z.string().min(2),
  status: z.boolean().default(true),
  tags: z.array(TagSchema).optional().default([]),
})

type CategoryFormValue = z.infer<typeof CategorySchema>

interface AddCategoryModalProps {
  open?: boolean
  onClose?: () => void
  defaultValues?: Partial<CategoryFormValue>
  getNewRecord?: (response: CategoryFormValue) => void
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  open,
  onClose,
  getNewRecord,
}) => {
  const {
    errors,
    openDialog,
    isSubmitting,
    selectedRow,
    newRecord,
    register,
    handleSubmit,
    setValue,
    handleReset,
    defaultOnClose,
    control,
    Controller,
    api,
    queryClient,
  } = useAddModel<CategoryFormValue>(
    "category",
    CategorySchema as any,
    "category",
    onClose
  )

  const { tags, isLoading: tagLoading } = useTags()

  useEffect(() => {
    if (selectedRow) {
      // Map status from "active"/"inactive" to boolean
      const statusValue =
        selectedRow.status === "Active" || selectedRow.status === "active"
      setValue("name", selectedRow.name || "")
      setValue("status", statusValue)
      setValue("tags", selectedRow.tags || [])
    } else {
      // Reset form when creating new
      setValue("name", "")
      setValue("status", true)
      setValue("tags", [])
    }
  }, [selectedRow, setValue])

  useEffect(() => {
    if (newRecord && getNewRecord) {
      getNewRecord(newRecord)
    }
  }, [newRecord, getNewRecord])

  const handleFormSubmit = async (data: any) => {
    const method = selectedRow ? "PUT" : "POST"
    const apiUrl = selectedRow ? `category/${selectedRow.id}` : "category"

    // Extract tags separately - they will be assigned via separate endpoint
    // Ensure tags is always an array
    const categoryTags = data.tags || []
    const { tags: _, ...categoryData } = data

    // Convert boolean status to "active"/"inactive" string
    const status = data.status === true ? "active" : "inactive"

    // Create/update category first (without tags - they're handled separately)
    const categoryResponse = await api(apiUrl, {
      method,
      body: JSON.stringify({ ...categoryData, status }),
    })

    if (categoryResponse) {
      // Assign tags after category is created/updated
      const categoryId = selectedRow?.id || (categoryResponse as any)?.data?.id
      if (categoryId) {
        const tagIds =
          Array.isArray(categoryTags) && categoryTags.length > 0
            ? categoryTags.map((tag) =>
                typeof tag === "object" && tag.id ? tag.id : tag
              )
            : []

        // Assign tags via separate endpoint (even if empty array to clear tags)
        await api(`category/${categoryId}/assign-tags`, {
          method: "POST",
          body: JSON.stringify({
            tag_ids: tagIds,
          }),
        })
      }

      // Invalidate queries and show success message
      queryClient.invalidateQueries({ queryKey: ["category"] })
      toast.success(
        `Category successfully ${selectedRow ? "updated" : "added"}`
      )
      onClose ? onClose() : defaultOnClose()
    }
  }

  return (
    <Modal open={open ? open : openDialog} onClose={defaultOnClose}>
      <Box
        sx={{
          width: { xs: "95vw", sm: "90vw", md: 600 },
          margin: { xs: "50px auto", md: "100px auto" },
          padding: { xs: 1, sm: 2 },
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
            flexWrap: "wrap",
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
              color: "black",
            }}
          >
            {selectedRow ? "Update Category" : "Add New Category"}
          </Typography>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </Box>

        {/* Form */}
        <Box
          sx={{ padding: 2 }}
          component="form"
          onSubmit={handleSubmit(handleFormSubmit as any)}
          noValidate
        >
          <Grid container spacing={2}>
            <Grid item xs={9} md={9}>
              <TextField
                fullWidth
                label="Name"
                sx={{ marginBottom: 2 }}
                error={!!errors.name}
                helperText={errors.name?.message?.toString() || ""}
                {...register("name")}
              />
            </Grid>
            <Grid item xs={3} md={3}>
              <Controller
                name="status"
                control={control}
                defaultValue={true}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={
                          field.value === true || field.value === "active"
                        }
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      field.value === true || field.value === "active"
                        ? "Active"
                        : "Inactive"
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <MultiSelect
                name="tags"
                label="Tags"
                options={tags}
                control={control}
                loading={tagLoading}
                defaultValues={selectedRow?.tags || []}
                controller={Controller}
                optionLabel={["name"]}
                error={!!errors.tags}
                helperText={errors.tags?.message?.toString() || ""}
              />
            </Grid>
          </Grid>

          {/* Buttons */}
          <Grid item xs={12} mt={1}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                onClick={handleReset}
                variant="text"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Reset
              </Button>

              <Button
                onClick={onClose ? onClose : defaultOnClose}
                variant="outlined"
                color="error"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                  color: "#fff !important",
                }}
              >
                {selectedRow ? "Update" : "Create"}
              </Button>
            </Box>
          </Grid>
        </Box>
      </Box>
    </Modal>
  )
}

export default AddCategoryModal
